import { BarChart } from '@mui/x-charts';
import { Graph } from '../Dashboard/dashboardTypes';
import { useEffect, useMemo, useState } from 'react';
import { CHART_BLUE } from '../../theme/theme-colors';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ChartSeriesToggleButtons } from './ChartSeriesToggleButtons';
import type { ChartSeriesLabelOptions } from './chartSeriesLabel';
import { chartSeriesLabel } from './chartSeriesLabel';
import { coerceLineChartYValue } from './LineGraph';

interface BarGraphProps {
  graph: Graph;
  chartSeriesLabelOptions?: ChartSeriesLabelOptions;
}

interface BarChartSeriesItem {
  data: Array<number | null>;
  color: string;
  label: string;
}

/**
 * Live bar view: one band per `graph.series` dataKey, values from the latest row
 * (same shape as line charts: `pythonStream` fields on each `graph.data` point).
 */
const BarGraph = ({ graph, chartSeriesLabelOptions }: BarGraphProps) => {
  const { t } = useTranslation();
  const categories = useMemo(() => graph.series.map((s) => s.dataKey), [graph.series]);

  const seriesKeysSig = categories.join('\0');

  const [activeKeys, setActiveKeys] = useState<string[]>(() => categories);

  useEffect(() => {
    if (!seriesKeysSig) {
      setActiveKeys([]);
      return;
    }
    setActiveKeys(seriesKeysSig.split('\0'));
  }, [graph.key, seriesKeysSig]);

  const formattedXAxis = useMemo(() => [{ barGapRatio: -1, scaleType: 'band' as const, data: categories }], [categories]);

  const series: BarChartSeriesItem[] | undefined = useMemo(() => {
    if (!graph.data?.length || !graph.series.length) return undefined;
    const last = graph.data[graph.data.length - 1] as Record<string, unknown>;
    const values = categories.map((k) => (activeKeys.includes(k) ? coerceLineChartYValue(last[k]) : 0));
    return graph.series.map((s, rowIndex) => ({
      data: values.map((val, colIndex) => (rowIndex === colIndex ? val : 0)),
      color: s.color ?? CHART_BLUE,
      label: chartSeriesLabel(t, s.dataKey, chartSeriesLabelOptions),
    }));
  }, [graph.data, graph.series, categories, activeKeys, t, chartSeriesLabelOptions]);

  const activeDataKeys = useMemo(() => new Set(activeKeys), [activeKeys]);

  const alterSeries = (dataKey: string) => {
    setActiveKeys((prev) => (prev.includes(dataKey) ? prev.filter((k) => k !== dataKey) : [...prev, dataKey]));
  };

  return (
    series &&
    graph?.data?.length > 0 && (
      <Box display="flex" flexDirection="column" width="100%" height="100%">
        <Box flexGrow={1} minHeight={0}>
          <BarChart xAxis={formattedXAxis} series={series} slotProps={{ tooltip: { trigger: 'item' } }} />
        </Box>
        {graph && (
          <ChartSeriesToggleButtons
            series={graph.series}
            activeDataKeys={activeDataKeys}
            chartSeriesLabelOptions={chartSeriesLabelOptions}
            onToggle={alterSeries}
          />
        )}
      </Box>
    )
  );
};

export default BarGraph;
